import React, { useState } from 'react';
import { ModalWrapper } from '../../../components/ModalWrapper';
import styled from 'styled-components';
import { Button, TextField, Checkbox, FormControlLabel, CircularProgress } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon } from '@fortawesome/free-solid-svg-icons';
import { DarkMode, BG_COLOR } from '../types/darkMode';

const PRESET_COLORS = ['E5E5E5', 'CACACA', 'F2F4F9', '8D8FA7', '9CB2C1'];

interface IProps {
  imageUrl: string;
  originalImageUrl: string | null;
  vocId: number;
  vocTitle: string;
  itemId: number;
  alternativeNumber: 1 | 2;
  hasAlpha: boolean;
  onClose: () => void;
  onSubmit: (
    itemId: number,
    alternativeNumber: number,
    additionalPrompt: string,
    useEnhancedBase: boolean,
    flattenBgColor: string | null,
  ) => Promise<void>;
}

export const EditImageModal: React.FC<IProps> = ({
  imageUrl,
  originalImageUrl,
  vocId,
  vocTitle,
  itemId,
  alternativeNumber,
  hasAlpha,
  onClose,
  onSubmit,
}) => {
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [useEnhancedBase, setUseEnhancedBase] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(DarkMode.LIGHT);
  const [flattenToColor, setFlattenToColor] = useState(false);
  const [bgColor, setBgColor] = useState('E5E5E5');

  const toggleDarkMode = () => {
    setDarkMode((d) => (d === DarkMode.LIGHT ? DarkMode.DARK : DarkMode.LIGHT));
  };

  const handleFlattenToggle = (checked: boolean) => {
    setFlattenToColor(checked);
    if (checked) {
      setUseEnhancedBase(false);
    } else {
      setUseEnhancedBase(true);
    }
  };

  const displayedImageUrl = !useEnhancedBase && originalImageUrl ? originalImageUrl : imageUrl;

  const handleSubmit = async () => {
    if (!flattenToColor && !additionalPrompt.trim()) {
      setError('Please enter modification instructions');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onSubmit(
        itemId,
        alternativeNumber,
        additionalPrompt.trim(),
        useEnhancedBase,
        flattenToColor ? bgColor : null,
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit remix request');
      setIsLoading(false);
    }
  };

  return (
    <ModalWrapper
      onClose={onClose}
      title={`Edit/Remix - Alternative ${alternativeNumber}`}
      width={700}
      height={hasAlpha ? 850 : 780}
      disableBackdropClick={isLoading}
    >
      <ContentWrapper>
        <MetadataSection>
          <MetadataItem>
            <strong>Vocabulary:</strong> {vocTitle} (ID: {vocId})
          </MetadataItem>
          <MetadataItem>
            <strong>Alternative:</strong> {alternativeNumber}
          </MetadataItem>
        </MetadataSection>

        <ImageSection>
          <ImageContainer $bgColor={flattenToColor ? `#${bgColor}` : BG_COLOR[darkMode]}>
            <StyledImage
              src={displayedImageUrl}
              alt={`${vocTitle} - Alternative ${alternativeNumber}`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                if (target.parentElement) {
                  target.parentElement.innerHTML =
                    "<div style='color: #999; text-align: center;'>Image not found</div>";
                }
              }}
            />
          </ImageContainer>
          {!flattenToColor && (
            <ToggleButton onClick={toggleDarkMode} title="Toggle light/dark background">
              <FontAwesomeIcon icon={faMoon} />
            </ToggleButton>
          )}
        </ImageSection>

        <FormSection>
          <TextField
            label="Additional Instructions"
            placeholder="E.g., Make it more colorful, Add more detail to the background, Simplify the composition..."
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            value={additionalPrompt}
            onChange={(e) => setAdditionalPrompt(e.target.value)}
            disabled={isLoading}
            error={!!error}
            helperText={error || 'Describe how you want to modify this image'}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={useEnhancedBase}
                onChange={(e) => setUseEnhancedBase(e.target.checked)}
                disabled={isLoading || flattenToColor}
                color="primary"
              />
            }
            label="Use enhanced image as base (uncheck to start from original PNG)"
          />

          {hasAlpha && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={flattenToColor}
                    onChange={(e) => handleFlattenToggle(e.target.checked)}
                    disabled={isLoading}
                    color="primary"
                  />
                }
                label="Flatten transparency (fill with background color)"
              />
              {flattenToColor && (
                <ColorPickerRow>
                  {PRESET_COLORS.map((color) => (
                    <ColorSwatch
                      key={color}
                      $color={`#${color}`}
                      $selected={bgColor === color}
                      onClick={() => setBgColor(color)}
                      title={`#${color}`}
                    />
                  ))}
                  <OtherColorWrapper>
                    <OtherColorLabel>Other:</OtherColorLabel>
                    <CustomColorInput
                      type="color"
                      value={`#${bgColor}`}
                      onChange={(e) => setBgColor(e.target.value.replace('#', '').toUpperCase())}
                      title="Custom color"
                    />
                  </OtherColorWrapper>
                </ColorPickerRow>
              )}
            </>
          )}
        </FormSection>

        <ButtonSection>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading || (!flattenToColor && !additionalPrompt.trim())}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Submitting...' : 'Submit Remix'}
          </Button>
        </ButtonSection>
      </ContentWrapper>
    </ModalWrapper>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
`;

const MetadataSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const MetadataItem = styled.div`
  font-size: 14px;
  color: #333;

  strong {
    color: #000;
    margin-right: 8px;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageContainer = styled.div<{ $bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.$bgColor};
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  width: 500px;
  height: 380px;
  transition: background-color 0.2s ease;
`;

const ToggleButton = styled.button`
  align-self: flex-end;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  color: #607d8b;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    color: #455a64;
    background-color: #f5f5f5;
  }
`;

const StyledImage = styled.img`
  max-width: 490px;
  max-height: 370px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const ColorPickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 32px;
`;

const ColorSwatch = styled.button<{ $color: string; $selected: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 2px solid ${(p) => (p.$selected ? '#1976d2' : '#ccc')};
  background-color: ${(p) => p.$color};
  cursor: pointer;
  outline: none;
  box-shadow: ${(p) => (p.$selected ? '0 0 0 2px rgba(25,118,210,0.3)' : 'none')};
  transition: all 0.15s ease;

  &:hover {
    border-color: #1976d2;
    transform: scale(1.1);
  }
`;

const OtherColorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid #ccc;
`;

const OtherColorLabel = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

const CustomColorInput = styled.input`
  width: 28px;
  height: 28px;
  border: 2px solid #ccc;
  border-radius: 4px;
  padding: 0;
  cursor: pointer;
  background: none;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 2px;
  }

  &:hover {
    border-color: #1976d2;
  }
`;
